import React, { useState } from 'react';
import { Link } from '../../Link';
import { useMenuPulse } from '../menu/useMenuPulse';
import type { MenuPulse } from '../../../server/routes/menu-pulse';
import { PRIMARY_NAV_LINKS, primaryNavHref, primaryNavHrefByKey, primaryNavLabel, type PrimaryNavLocale } from '../primary-nav-links';
import { resolveNavActiveSection, type NavActiveSection } from './active-section';
import { formatNavFigure } from './format-nav-figure';
import { navIndexMessages } from './nav-index-messages';
import { useScrollCollapse } from './useScrollCollapse';
import { NEW_TODAY_FIRST_SEEN_CODE } from '../../../utils/search-router/first-seen-threshold';
import { computeUpdatedMinutesAgo } from './updated-minutes-ago';
import { useCountUp } from '../../../utils/motion/use-count-up';
import { shouldRecountNavFiguresThisPageLoad } from '../../../utils/motion/nav-figure-recount';
import {
  NAV_PART,
  STRIP_CONTAINER,
  STRIP_DIVIDER,
  STRIP_SCROLLER,
  STRIP_STAMP_DOT,
  STRIP_STAMP_WRAP,
  stripFigureLinkClass,
  stripFigureValueClass,
  STRIP_FIGURES_GROUP,
  STRIP_WORDS_GROUP,
  stripUnderlineClass,
  stripWordLinkClass,
} from './nav-styles';
import type { IntlShape } from 'react-intl';

type FormatMessage = IntlShape['formatMessage'];


interface FigureLinkConfig {
  key: 'openJobs' | 'newToday' | 'hiringEmployers' | 'remoteJobs';
  href: string;
  labelId: keyof typeof navIndexMessages;
  /** Which resolved active section, if any, this figure represents. `newToday` shares the
   * openJobs hub (it's the same /jobs route with a filter), so it's never its own section. */
  section: NavActiveSection | null;
}

/** The "new today" link: a stable URL (one address for crawlers, not a new timestamp per hour);
 * the relative code resolves at search time to the same crawl-schedule-anchored boundary the
 * tile's own count uses (TEC-683), not a rolling 24h window. */
export function newTodayHref(locale: PrimaryNavLocale): string {
  return `${primaryNavHrefByKey('jobs', locale)}?firstSeenMin=${NEW_TODAY_FIRST_SEEN_CODE}`;
}

function buildFigureLinks(locale: PrimaryNavLocale): FigureLinkConfig[] {
  const jobsHref = primaryNavHrefByKey('jobs', locale);
  return [
    { key: 'openJobs', href: jobsHref, labelId: 'openJobs', section: 'openJobs' },
    { key: 'remoteJobs', href: primaryNavHrefByKey('remote', locale), labelId: 'remote', section: 'remote' },
    { key: 'newToday', href: newTodayHref(locale), labelId: 'newToday', section: null },
    { key: 'hiringEmployers', href: primaryNavHrefByKey('companies', locale), labelId: 'employersHiring', section: 'employers' },
  ];
}

const WORD_LINK_KEYS = ['locations', 'roles', 'salary', 'resources'] as const;

interface IndexStripProps {
  locale: PrimaryNavLocale;
  pathname: string;
  formatMessage: FormatMessage;
  /** SRP-only (TEC navbar redesign Phase 2 item 2.4): replaces the freshness stamp at the
   * strip's right end with the List | Map view toggle, since the SRP's own unfiltered row two
   * is this same strip (the tape only replaces it once a filter is applied, see
   * DesktopFilterCategories.tsx). Omitted everywhere else, so the stamp is unchanged there. */
  rightSlot?: React.ReactNode;
  /** Phase 3 item 3.2: the server-rendered pulse (`pageContext.navPulse`), so the strip's first
   * paint already has numbers instead of starting empty and fetching client-side. */
  initialPulse?: MenuPulse | null;
}

/**
 * Row two of the navbar live-index redesign (TEC navbar redesign Phase 1): the four market
 * figures, the four browse-by word links, and the freshness stamp. Renders at its fixed 34px
 * height immediately with labels and an empty figure slot (no layout shift) until useMenuPulse
 * resolves client-side; collapses on scroll down past ~64px, returns on scroll up. Out of scope
 * for the SRP (which keeps its own filter tape/TopFilters unchanged) and for the static
 * prerendered twin (ResourceNavbar): React-header only, per spec.
 */
export const IndexStrip: React.FC<IndexStripProps> = ({ locale, pathname, formatMessage, rightSlot, initialPulse }) => {
  const pulse = useMenuPulse(locale, true, initialPulse);
  const collapsed = useScrollCollapse(64);
  const activeSection = resolveNavActiveSection(pathname, locale);
  const figures = buildFigureLinks(locale);

  const figureValue = (key: FigureLinkConfig['key']): number | null => {
    if (!pulse) return null;
    if (key === 'openJobs') return pulse.openJobs;
    if (key === 'newToday') return pulse.newToday;
    if (key === 'hiringEmployers') return pulse.hiringEmployers;
    return pulse.remoteJobs;
  };

  // Round 10: number-roll animation for the four figures. Rule: never animate away from a number
  // that's already visible. `useMenuPulse`'s SSR seed (pageContext.navPulse) means the FIRST paint
  // already has the real number, so `recountOnMount` is the only thing that can make that
  // first-paint number move (a deliberate "quick recount", never a 0-to-N run-up); an ordinary
  // later pulse refresh always rolls from the old value to the new one. `shouldRecount` is
  // computed ONCE per page load (React's useState initializer form), not once per figure: all
  // four must agree on the same "is this the first view this session" answer, or the first figure
  // to check would mark the session as seen before its siblings' own checks ran (see
  // nav-figure-recount.ts's own comment).
  const [shouldRecount] = useState(() => shouldRecountNavFiguresThisPageLoad());
  const recountFor = (index: number) =>
    shouldRecount ? { enabled: true, fromRatio: 0.9, duration: 700, delayMs: index * 80 } : undefined;
  // Fixed count, one per figure (the recount delays follow the display order: openJobs,
  // remoteJobs, newToday, hiringEmployers): called unconditionally, at the top level, not inside the render .map() below,
  // so the number of hook calls never depends on `pulse`/`figures` at runtime.
  const displayedOpenJobs = useCountUp(figureValue('openJobs') ?? 0, { disabled: figureValue('openJobs') === null, recountOnMount: recountFor(0) });
  const displayedNewToday = useCountUp(figureValue('newToday') ?? 0, { disabled: figureValue('newToday') === null, recountOnMount: recountFor(2) });
  const displayedHiringEmployers = useCountUp(figureValue('hiringEmployers') ?? 0, { disabled: figureValue('hiringEmployers') === null, recountOnMount: recountFor(3) });
  const displayedRemoteJobs = useCountUp(figureValue('remoteJobs') ?? 0, { disabled: figureValue('remoteJobs') === null, recountOnMount: recountFor(1) });
  const displayedFigureValue = (key: FigureLinkConfig['key']): number | null => {
    if (figureValue(key) === null) return null;
    if (key === 'openJobs') return displayedOpenJobs;
    if (key === 'newToday') return displayedNewToday;
    if (key === 'hiringEmployers') return displayedHiringEmployers;
    return displayedRemoteJobs;
  };

  // Phase 3 item 3.1: a pulse older than an hour means the ten-minute cache failed to refresh
  // (should not happen); hide the stamp rather than print a stale, misleading time.
  const updatedMinutesAgo = pulse ? computeUpdatedMinutesAgo(pulse.updatedAt) : null;

  return (
    <div
      className={STRIP_CONTAINER}
      style={collapsed ? { height: 0, opacity: 0, borderTopColor: 'transparent' } : { height: 34, opacity: 1 }}
      aria-hidden={collapsed}
    >
      <div className={STRIP_SCROLLER}>
        <div className={STRIP_FIGURES_GROUP}>
          {figures.map((figure) => {
            const value = displayedFigureValue(figure.key);
            const isActive = figure.section !== null && figure.section === activeSection;
            return (
              <Link key={figure.key} href={figure.href} locale={locale} className={stripFigureLinkClass(isActive)} data-jc-nav-part={NAV_PART.stripFigure}>
                <b className={stripFigureValueClass(isActive)} data-jc-nav-part={NAV_PART.stripFigureValue}>
                  {value !== null ? formatNavFigure(value, locale) : ''}
                </b>
                {formatMessage(navIndexMessages[figure.labelId])}
                <span className={stripUnderlineClass(isActive)} aria-hidden />
              </Link>
            );
          })}
        </div>
        <span className={STRIP_DIVIDER} data-jc-nav-part={NAV_PART.stripDivider} aria-hidden />
        <div className={STRIP_WORDS_GROUP}>
          {WORD_LINK_KEYS.map((key) => {
            const link = PRIMARY_NAV_LINKS.find((l) => l.key === key)!;
            const isActive = activeSection === (key === 'roles' ? 'roles' : key === 'locations' ? 'locations' : key === 'salary' ? 'salary' : 'resources');
            return (
              <Link key={key} href={primaryNavHref(link, locale)} locale={locale} className={stripWordLinkClass(isActive)} data-jc-nav-part={NAV_PART.stripWord}>
                {primaryNavLabel(link, locale)}
                <span className={stripUnderlineClass(isActive)} aria-hidden />
              </Link>
            );
          })}
        </div>
      </div>
      <span className={STRIP_STAMP_WRAP} data-jc-nav-part={NAV_PART.stripStamp}>
        {rightSlot ?? (
          <>
            <span className={STRIP_STAMP_DOT} aria-hidden />
            {updatedMinutesAgo !== null ? formatMessage(navIndexMessages.updatedAgo, { minutes: updatedMinutesAgo }) : null}
          </>
        )}
      </span>
    </div>
  );
};
